import {
  type CreateTaskInput,
  type CreateTaskReminderInput,
  ListTaskAssigneesResponseSchema,
  type ListTasksQuery,
  ListTasksResponseSchema,
  type Task,
  TaskEmailSchema,
  TaskSchema,
  TaskReminderSchema,
  type TaskWithCaseSummary,
  type UpdateTaskReminderInput,
  type UpdateTaskInput,
} from "@recobra/schemas/task";

import { requestJson, requestJsonOrNoContent, requestVoid } from "./client";

const MAX_TASK_LIST_LIMIT = 100;

const listTasksSearchParams = (params: Partial<ListTasksQuery>): string => {
  const p = new URLSearchParams();
  if (params.limit != null) p.set("limit", String(params.limit));
  if (params.nextToken) p.set("nextToken", params.nextToken);
  if (params.assignedTo) p.set("assignedTo", params.assignedTo);
  if (params.status) p.set("status", params.status);
  if (params.contextType) p.set("contextType", params.contextType);
  if (params.contextId) p.set("contextId", params.contextId);
  if (params.type?.length) p.set("type", params.type.join(","));
  if (params.system?.length) p.set("system", params.system.join(","));

  return p.toString();
};

export const tasksApi = {
  listAssignees: (caseId?: string) =>
    requestJson(
      caseId ? `/tasks/assignees?${new URLSearchParams({ caseId })}` : "/tasks/assignees",
      ListTaskAssigneesResponseSchema,
    ),

  listTasks: (params?: Partial<ListTasksQuery>) => {
    const qs = listTasksSearchParams(params ?? {});

    return requestJson(qs ? `/tasks?${qs}` : "/tasks", ListTasksResponseSchema);
  },

  listAllTasks: async (params?: Omit<Partial<ListTasksQuery>, "limit" | "nextToken">) => {
    const items: TaskWithCaseSummary[] = [];
    let nextToken: string | undefined;

    do {
      const page = await tasksApi.listTasks({
        ...params,
        limit: MAX_TASK_LIST_LIMIT,
        nextToken,
      });
      items.push(...page.items);
      nextToken = page.nextToken;
    } while (nextToken);

    return items;
  },

  getTask: (id: string) => requestJson(`/tasks/${id}`, TaskSchema),

  /** Resolves to `null` when an open task of this type already exists (204 convergence). */
  createTask: (input: CreateTaskInput): Promise<Task | null> =>
    requestJsonOrNoContent("/tasks", TaskSchema, {
      method: "POST",
      body: JSON.stringify(input),
    }),

  updateTask: (id: string, input: UpdateTaskInput) =>
    requestJson(`/tasks/${id}`, TaskSchema, {
      method: "PATCH",
      body: JSON.stringify(input),
    }),

  createReminder: (id: string, input: CreateTaskReminderInput) =>
    requestJson(`/tasks/${id}/reminders`, TaskReminderSchema, {
      method: "POST",
      body: JSON.stringify(input),
    }),

  updateReminder: (id: string, reminderId: string, input: UpdateTaskReminderInput) =>
    requestJson(`/tasks/${id}/reminders/${reminderId}`, TaskReminderSchema, {
      method: "PATCH",
      body: JSON.stringify(input),
    }),

  removeReminder: (id: string, reminderId: string) =>
    requestVoid(`/tasks/${id}/reminders/${reminderId}`, { method: "DELETE" }),

  emailAssignee: (id: string) =>
    requestJson(`/tasks/${id}/assignee-email`, TaskEmailSchema, {
      method: "POST",
      body: JSON.stringify({ trigger: "manual" }),
    }),
};
